import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Tabs, Tab, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, CircularProgress,
  Alert, Grid, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment,
} from '@mui/material';
import {
  People as PeopleIcon, Restaurant as RestaurantIcon,
  CreditCard as CreditCardIcon, BarChart as StatsIcon,
  Add as AddIcon, ArrowBack as BackIcon, AccessTime as ClockIcon,
  TrendingUp as TrendingIcon, Wallet as WalletIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import cantinasService, { Cantina, Pulseira, Transacao, DashStats } from '../../services/cantinas.service';
import api from '../../services/api';

const fmtCVE = (v: number) =>
  new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(v || 0);

const fmtDate = (s: string) =>
  new Date(s).toLocaleString('pt-CV', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const tipoColor: Record<string, 'success' | 'primary' | 'warning' | 'default'> = {
  TOPUP: 'success', PURCHASE: 'primary', REFUND: 'warning', ADJUSTMENT: 'default',
};
const tipoLabel: Record<string, string> = {
  TOPUP: 'Carregamento', PURCHASE: 'Compra', REFUND: 'Reembolso', ADJUSTMENT: 'Ajuste',
};

export default function EscolaPage() {
  const { id } = useParams<{ id: string }>();
  const escolaId = Number(id);
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState<DashStats | null>(null);
  const [cantinas, setCantinas] = useState<Cantina[]>([]);
  const [pulseiras, setPulseiras] = useState<Pulseira[]>([]);
  const [transacoes, setTransacoes] = useState<Transacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cantina modal
  const [cantinaModal, setCantinaModal] = useState(false);
  const [cantinaForm, setCantinaForm] = useState({ nome: '', responsavel: '', horario_abertura: '07:00', horario_fecho: '15:00' });
  const [saving, setSaving] = useState(false);

  // Student modal
  const [studentModal, setStudentModal] = useState(false);
  const [studentForm, setStudentForm] = useState({ nfc_uid: '', aluno_nome: '', turma: '', pai_nome: '', pai_telefone: '', pai_uid: '', limite_diario: '500' });
  const [studentSaving, setStudentSaving] = useState(false);
  const [studentError, setStudentError] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, p] = await Promise.all([
        cantinasService.getDashboard(escolaId),
        cantinasService.getCantinas(escolaId),
        cantinasService.getPulseiras(escolaId),
      ]);
      setStats(s); setCantinas(c); setPulseiras(p);
    } catch { setError('Erro ao carregar dados'); }
    finally { setLoading(false); }
  }, [escolaId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (tab === 3 && transacoes.length === 0) {
      cantinasService.getTransacoes(escolaId).then(r => setTransacoes(r.data));
    }
  }, [tab, escolaId, transacoes.length]);

  async function saveCantina() {
    setSaving(true);
    try {
      await cantinasService.createCantina(escolaId, cantinaForm);
      setCantinaModal(false);
      setCantinaForm({ nome: '', responsavel: '', horario_abertura: '07:00', horario_fecho: '15:00' });
      loadAll();
    } catch { } finally { setSaving(false); }
  }

  async function saveStudent() {
    setStudentSaving(true); setStudentError(null);
    try {
      const r = await api.post(`/cantinas/escola/${escolaId}/wristbands`, { ...studentForm, limite_diario: Number(studentForm.limite_diario) });
      if (!r.data?.success) throw new Error(r.data?.error || 'Erro');
      setStudentModal(false);
      setStudentForm({ nfc_uid: '', aluno_nome: '', turma: '', pai_nome: '', pai_telefone: '', pai_uid: '', limite_diario: '500' });
      loadAll();
    } catch (e: any) {
      setStudentError(e.response?.data?.error || e.message || 'Erro ao registar aluno');
    } finally { setStudentSaving(false); }
  }

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/cantinas')} sx={{ color: 'text.secondary' }}>
          Cantinas
        </Button>
        <Typography variant="h5" fontWeight={700}>Escola #{escolaId}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<StatsIcon />} label="Dashboard" iconPosition="start" />
        <Tab icon={<PeopleIcon />} label={`Alunos (${pulseiras.length})`} iconPosition="start" />
        <Tab icon={<RestaurantIcon />} label={`Cantinas (${cantinas.length})`} iconPosition="start" />
        <Tab icon={<CreditCardIcon />} label="Transações" iconPosition="start" />
      </Tabs>

      {/* ── DASHBOARD ── */}
      {tab === 0 && stats && (
        <Grid container spacing={2}>
          {[
            { label: 'Alunos activos', value: stats.pulseiras_ativas, icon: <PeopleIcon />, color: '#6233A0' },
            { label: 'Saldo total', value: fmtCVE(stats.saldo_total), icon: <WalletIcon />, color: '#10B981' },
            { label: 'Receita hoje', value: fmtCVE(stats.receita_hoje), icon: <TrendingIcon />, color: '#0D7F9F' },
            { label: 'Pagamentos hoje', value: stats.pagamentos_hoje, icon: <CreditCardIcon />, color: '#F59E0B' },
            { label: 'Carregamentos mês', value: fmtCVE(stats.carregamentos_mes), icon: <TrendingIcon />, color: '#EF4444' },
            { label: 'Total alunos', value: stats.total_alunos, icon: <PeopleIcon />, color: '#6B7280' },
          ].map(s => (
            <Grid item xs={12} sm={6} md={4} key={s.label}>
              <Paper sx={{ p: 2.5, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: s.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color }}>
                  {s.icon}
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── ALUNOS ── */}
      {tab === 1 && (
        <>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button variant="contained" startIcon={<AddIcon />}
              sx={{ bgcolor: '#6233A0', '&:hover': { bgcolor: '#4e2880' } }}
              onClick={() => { setStudentModal(true); setStudentError(null); }}>
              Registar Aluno
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  {['Aluno', 'Turma', 'NFC UID', 'Saldo', 'Gasto hoje', 'Limite diário', 'Estado'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pulseiras.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>Sem alunos registados</TableCell></TableRow>
                ) : pulseiras.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{p.aluno_nome}</Typography>
                      {p.pai_nome && <Typography variant="caption" color="text.secondary">{p.pai_nome}</Typography>}
                    </TableCell>
                    <TableCell>{p.turma || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{p.nfc_uid}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{fmtCVE(p.saldo)}</TableCell>
                    <TableCell>{fmtCVE(p.gasto_hoje)}</TableCell>
                    <TableCell>{fmtCVE(p.limite_diario)}</TableCell>
                    <TableCell>
                      <Chip label={p.status} size="small"
                        color={p.status === 'ACTIVE' ? 'success' : p.status === 'BLOCKED' ? 'error' : 'default'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ── CANTINAS ── */}
      {tab === 2 && (
        <>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button variant="contained" startIcon={<AddIcon />}
              sx={{ bgcolor: '#6233A0', '&:hover': { bgcolor: '#4e2880' } }}
              onClick={() => setCantinaModal(true)}>
              Nova Cantina
            </Button>
          </Box>
          <Grid container spacing={2}>
            {cantinas.length === 0 ? (
              <Grid item xs={12}>
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                  <RestaurantIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">Sem cantinas configuradas</Typography>
                </Paper>
              </Grid>
            ) : cantinas.map(c => (
              <Grid item xs={12} sm={6} md={4} key={c.id}>
                <Paper sx={{ p: 2.5, borderRadius: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography fontWeight={700}>{c.nome}</Typography>
                    <Chip label={c.ativo ? 'Activa' : 'Inactiva'} size="small" color={c.ativo ? 'success' : 'default'} />
                  </Box>
                  {c.responsavel && <Typography variant="body2" color="text.secondary">{c.responsavel}</Typography>}
                  <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                    <ClockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {c.horario_abertura?.slice(0, 5)} – {c.horario_fecho?.slice(0, 5)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* ── TRANSAÇÕES ── */}
      {tab === 3 && (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {['Data', 'Tipo', 'Aluno', 'Cantina', 'Valor', 'Saldo depois', 'Descrição'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {transacoes.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>Sem transações</TableCell></TableRow>
              ) : transacoes.map(t => (
                <TableRow key={t.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(t.created_at)}</TableCell>
                  <TableCell><Chip label={tipoLabel[t.tipo] || t.tipo} size="small" color={tipoColor[t.tipo] || 'default'} /></TableCell>
                  <TableCell>{t.aluno_nome || '—'}</TableCell>
                  <TableCell>{t.cantina_nome || '—'}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: t.tipo === 'TOPUP' ? 'success.main' : 'inherit' }}>
                    {t.tipo === 'TOPUP' ? '+' : '-'}{fmtCVE(t.valor)}
                  </TableCell>
                  <TableCell>{t.saldo_depois != null ? fmtCVE(t.saldo_depois) : '—'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{t.descricao || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal Cantina */}
      <Dialog open={cantinaModal} onClose={() => setCantinaModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700}>Nova Cantina</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12}>
              <TextField label="Nome *" fullWidth value={cantinaForm.nome} onChange={e => setCantinaForm({ ...cantinaForm, nome: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Responsável" fullWidth value={cantinaForm.responsavel} onChange={e => setCantinaForm({ ...cantinaForm, responsavel: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Abertura" type="time" fullWidth value={cantinaForm.horario_abertura} onChange={e => setCantinaForm({ ...cantinaForm, horario_abertura: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Fecho" type="time" fullWidth value={cantinaForm.horario_fecho} onChange={e => setCantinaForm({ ...cantinaForm, horario_fecho: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCantinaModal(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveCantina} disabled={saving || !cantinaForm.nome}
            startIcon={saving ? <CircularProgress size={16} /> : null}
            sx={{ bgcolor: '#6233A0', '&:hover': { bgcolor: '#4e2880' } }}>
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Aluno */}
      <Dialog open={studentModal} onClose={() => setStudentModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700}>Registar Aluno</DialogTitle>
        <DialogContent dividers>
          {studentError && <Alert severity="error" sx={{ mb: 2 }}>{studentError}</Alert>}
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12}>
              <TextField label="UID NFC da Pulseira *" fullWidth value={studentForm.nfc_uid} onChange={e => setStudentForm({ ...studentForm, nfc_uid: e.target.value })} helperText="Ex: AA:BB:CC:DD" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Nome do Aluno *" fullWidth value={studentForm.aluno_nome} onChange={e => setStudentForm({ ...studentForm, aluno_nome: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Turma" fullWidth value={studentForm.turma} onChange={e => setStudentForm({ ...studentForm, turma: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Limite Diário (CVE)" type="number" fullWidth value={studentForm.limite_diario}
                onChange={e => setStudentForm({ ...studentForm, limite_diario: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">CVE</InputAdornment> }} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Nome do Pai/Mãe" fullWidth value={studentForm.pai_nome} onChange={e => setStudentForm({ ...studentForm, pai_nome: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Telefone do Pai/Mãe" fullWidth value={studentForm.pai_telefone} onChange={e => setStudentForm({ ...studentForm, pai_telefone: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="UID Firebase do Pai (app Pagali)" fullWidth value={studentForm.pai_uid} onChange={e => setStudentForm({ ...studentForm, pai_uid: e.target.value })} helperText="Permite ao pai ver e carregar a pulseira na app" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setStudentModal(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveStudent} disabled={studentSaving || !studentForm.nfc_uid || !studentForm.aluno_nome}
            startIcon={studentSaving ? <CircularProgress size={16} /> : null}
            sx={{ bgcolor: '#6233A0', '&:hover': { bgcolor: '#4e2880' } }}>
            Registar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
